import React, { useEffect, useState } from 'react';
import { Fields, FormRef } from 'common/form';
import Yup from 'utils/validation';
import { BaseQuestion } from './BaseQuestion';
import { QuestionWithId, QuestionUpdates } from 'pages/CampaignCreation/types';
import { DocumentQuestionView } from '../QuestionViews';

const documentQuestionSchema = Yup.object({
  documentTypes: Yup.array()
    .of(Yup.string())
    .min(1, 'Enter document description.')
    .default([]),
  content: Yup.string().default(''),
  isMandatory: Yup.boolean().default(false)
});
export type DocumentQuestionFormData = Yup.InferType<
  typeof documentQuestionSchema
>;

const documentQuestionFields: Fields<DocumentQuestionFormData> = {
  documentTypes: {
    type: 'chipInput',
    label: 'Ask for document(s)',
    subLabel:
      'E.g. valid driving licence. Press ENTER to add multiple documents.'
  },
  isMandatory: {
    type: 'checkbox',
    checkboxLabel: 'Candidate must provide these documents to progress',
    containerSx: {
      mt: 0
    }
  },
  content: {
    type: 'text',
    hidden: true // Hidden field to satisfy BaseQuestion requirements
  }
};

type Props = {
  question: QuestionWithId;
  isFirst: boolean;
  isLast: boolean;
  removeQuestion: (id: string) => void;
  moveQuestion: ({
    id,
    direction
  }: {
    id: string;
    direction: 'up' | 'down';
  }) => void;
  updateQuestion: (id: string, updates: QuestionUpdates) => void;
};

export const DocumentQuestion: React.FC<Props> = ({
  question,
  isFirst,
  isLast,
  removeQuestion,
  moveQuestion,
  updateQuestion
}) => {
  const { id, content = '', documentTypes = [], isMandatory } = question;

  const [initialDocumentTypes, setInitialDocumentTypes] =
    useState<string[]>(documentTypes);

  // Update initial document types when props change
  useEffect(() => {
    setInitialDocumentTypes(documentTypes);
  }, [documentTypes]);

  const handleWatch = (
    formRef: FormRef<DocumentQuestionFormData>,
    questionId: string
  ) => {
    return formRef.watch(
      (value: DocumentQuestionFormData, { name }: { name: string }) => {
        if (name === 'documentTypes' && value.documentTypes) {
          // Filter any empty strings
          const currentDocuments = value.documentTypes.filter(
            Boolean
          ) as string[];

          // Skip if no changes
          if (
            JSON.stringify(currentDocuments) ===
            JSON.stringify(initialDocumentTypes)
          ) {
            return;
          }

          // Update initial state for next comparison
          setInitialDocumentTypes(currentDocuments);

          const updates: QuestionUpdates = {
            documentTypes: currentDocuments
          };

          // Add content update if needed
          if (value.content !== content) {
            updates.content = value.content;
          }

          updateQuestion(questionId, updates);
        }

        // Handle isMandatory checkbox changes
        if (name === 'isMandatory' && value.isMandatory !== undefined) {
          updateQuestion(questionId, { isMandatory: value.isMandatory });
        }
      }
    ).unsubscribe;
  };

  const handleCancel = (initialValues: DocumentQuestionFormData) => {
    if (
      !initialValues.documentTypes ||
      initialValues.documentTypes.length === 0
    ) {
      // Delete the question if it's a new question (empty documentTypes)
      removeQuestion(id);
      return true;
    }

    const docTypes = initialValues.documentTypes.filter(Boolean) as string[];
    setInitialDocumentTypes(docTypes);

    const updates: QuestionUpdates = {
      documentTypes: docTypes
    };

    if (initialValues.isMandatory !== undefined) {
      updates.isMandatory = initialValues.isMandatory;
    }

    updateQuestion(id, updates);

    return true;
  };

  const renderViewMode = (editing: boolean) => {
    if (editing) return null;

    return (
      <DocumentQuestionView
        documentTypes={documentTypes}
        isMandatory={isMandatory}
      />
    );
  };

  // For document questions, we determine if it should be in edit mode based on whether documentTypes are empty
  const shouldBeInEditMode = documentTypes.length === 0;

  return (
    <BaseQuestion<DocumentQuestionFormData>
      id={id}
      isFirst={isFirst}
      isLast={isLast}
      editByDefault={shouldBeInEditMode}
      setQuestionValue={(id, value) => updateQuestion(id, { content: value })}
      onDelete={() => removeQuestion(id)}
      moveQuestion={moveQuestion}
      schema={documentQuestionSchema}
      fields={documentQuestionFields}
      formDefaultValues={{
        documentTypes,
        content: '',
        isMandatory: isMandatory || false
      }}
      renderViewMode={renderViewMode}
      onWatch={handleWatch}
      onCancel={handleCancel}
    />
  );
};
